import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Auth from 'modules/Auth';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import { PermissionsEnum } from 'consts/permissions';
import { FeatureFlags } from 'components/common/types/Features.types';
import { changeCampaignType } from 'store/dealManagement/reducer';
import { Store } from 'components/common/types/Store.types';
import { CampaignType } from 'components/pages/Planner/PlannerSections/types';
import { checkFeaturePermission } from 'utils/checkFeaturePermission';
import { getIsNewDeal } from 'store/dealManagement/selectors/getIsNewDeal';
import { UseCampaignTypeSwitch } from './useCampaignTypeSwitch.types';

export const PLANNER_URLS = {
  [CampaignType.DIRECT]: '/planner/direct-sales',
  [CampaignType.PROGRAMMATIC]: '/planner/programmatic',
};

export const useCampaignTypeSwitch = (): UseCampaignTypeSwitch => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isNewDeal = useSelector(getIsNewDeal);
  const campaignType = useSelector((state: Store) => state.dealManagement.campaignType);

  const arePermissionsEnabled = useHasFeatureAccess(FeatureFlags.PLANNERS_PERMISSIONS);

  const hasDirectSalesPlannerEnabled = checkFeaturePermission(
    arePermissionsEnabled,
    Auth.hasPermission(PermissionsEnum.DIRECT_SALES_PERMISSION),
  );
  const hasProgrammaticPlannerEnabled = checkFeaturePermission(
    arePermissionsEnabled,
    Auth.hasPermission(PermissionsEnum.PROGRAMMATIC_PERMISSION),
  );

  const handleCampaignTypeOnMount = (): void => {
    if (!isNewDeal) return;

    if (hasDirectSalesPlannerEnabled && pathname.includes(PLANNER_URLS[CampaignType.DIRECT])) {
      dispatch(changeCampaignType(CampaignType.DIRECT));
      return;
    }

    dispatch(changeCampaignType(CampaignType.PROGRAMMATIC));
  };

  useEffect(() => {
    handleCampaignTypeOnMount();
  }, [pathname, isNewDeal]);

  const onChange = (value: CampaignType): void => {
    navigate(PLANNER_URLS[value]);
    dispatch(changeCampaignType(value));
  };

  const options = [
    { text: 'Programmatic', value: CampaignType.PROGRAMMATIC, isActive: hasProgrammaticPlannerEnabled },
    { text: 'Direct', value: CampaignType.DIRECT, isActive: hasDirectSalesPlannerEnabled },
  ].filter((option) => option.isActive);

  return {
    campaignType,
    isNewDeal,
    onChange,
    options,
    handleCampaignTypeOnMount,
  };
};
